import React from 'react';
import { useNavigate } from 'react-router-dom';
import ServiceLayout from '../../components/ServiceLayout';
import { 
  RocketLaunchIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  AcademicCapIcon,
  ChartBarIcon,
  ServerIcon,
  CloudIcon,
  CodeBracketIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';

const ITProjects: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <ClipboardDocumentListIcon />,
      title: 'Project Planning & Strategy',
      description: 'Comprehensive project planning with detailed roadmaps and strategic alignment.',
      benefits: [
        'Detailed project scoping',
        'Timeline and milestone planning',
        'Risk assessment and mitigation',
        'Budget planning and control',
        'Stakeholder communication plans'
      ]
    },
    {
      icon: <CloudIcon />,
      title: 'System Integration & Migration',
      description: 'Seamless integration of new systems with existing infrastructure and data migration.',
      benefits: [
        'Legacy system modernization',
        'Data migration and validation',
        'API development and integration',
        'System compatibility testing',
        'Minimal downtime transitions'
      ]
    },
    {
      icon: <CodeBracketIcon />,
      title: 'Custom Software Development',
      description: 'Tailored software solutions designed to meet your specific business requirements.',
      benefits: [
        'Custom application development',
        'Database design and optimization',
        'User interface and experience design',
        'Mobile app development',
        'Web application development'
      ]
    },
    {
      icon: <ServerIcon />,
      title: 'Infrastructure Upgrades',
      description: 'Modernize your IT infrastructure with the latest technologies and best practices.',
      benefits: [
        'Network infrastructure upgrades',
        'Server hardware and software updates',
        'Security system implementations',
        'Performance optimization',
        'Scalability planning'
      ]
    },
    {
      icon: <AcademicCapIcon />,
      title: 'Training & Knowledge Transfer',
      description: 'Comprehensive training programs to ensure your team can effectively use new systems.',
      benefits: [
        'User training programs',
        'Administrator training',
        'Documentation creation',
        'Knowledge transfer sessions',
        'Ongoing support planning'
      ]
    },
    {
      icon: <WrenchScrewdriverIcon />,
      title: 'Ongoing Support & Maintenance',
      description: 'Post-implementation support to ensure your projects continue to deliver value.',
      benefits: [
        'System maintenance and updates',
        'Performance monitoring',
        'Issue resolution and support',
        'Feature enhancements',
        'Continuous improvement'
      ]
    }
  ];

  const process = {
    title: 'Our Project Delivery Process',
    description: 'A proven methodology that ensures successful project delivery on time and within budget.',
    steps: [
      {
        title: 'Discovery & Requirements',
        description: 'Thorough analysis of your business needs, current systems, and project objectives to define clear requirements and success criteria.'
      },
      {
        title: 'Planning & Design',
        description: 'Create detailed project plans, system architecture, and design specifications with clear timelines and deliverables.'
      },
      {
        title: 'Development & Implementation',
        description: 'Execute the project plan with regular progress updates, quality assurance testing, and stakeholder communication.'
      },
      {
        title: 'Testing & Deployment',
        description: 'Comprehensive testing in staging environments followed by carefully managed production deployment.'
      },
      {
        title: 'Training & Handover',
        description: 'Provide thorough training and documentation to ensure smooth transition and ongoing success.'
      }
    ]
  };

  const stats = [
    { value: '95%', label: 'On-Time Delivery' },
    { value: '100+', label: 'Projects Completed' },
    { value: '<60 days', label: 'Average Lead Time' }
  ];

  const cta = {
    title: 'Ready to Start Your Next IT Project?',
    description: 'Let\'s discuss your project requirements and how we can help you achieve your technology goals.',
    buttonText: 'Launch Your Project',
    onAction: () => navigate('/contact')
  };

  return (
    <ServiceLayout
      serviceName="IT Projects"
      icon={<RocketLaunchIcon />}
      title="Expert IT Project Delivery"
      subtitle="Storage upgrades, network redeployments, server migrations—we handle complex IT infrastructure projects with fixed pricing and zero drama. On time, on budget, every time."
      heroImage="/images/services/it-transformation.webp"
      stats={stats}
      features={features}
      process={process}
      cta={cta}
    />
  );
};

export default ITProjects;